export const AUTHORIZATION_MIGRATION_STATUSES = ['complete', 'pending', 'notApplicable'];
/**
 * Lightweight inventory of current authorization boundaries. "Pending"
 * means the canonical capability exists but current production enforcement
 * still uses the documented V1 mechanism. This map does not grant access.
 */
export const AUTHORIZATION_SURFACE_COVERAGE = Object.freeze([
    {
        id: 'unit-map',
        area: 'Unit Map',
        locations: ['src/views/UnitMap.tsx', 'src/hooks/useUnitMap.ts', 'functions/src/unitMap.ts'],
        legacyMechanism: 'None. The dedicated callable resolves the Unit Map capability from trusted tenant membership and returns a sanitized Unit projection.',
        capabilityKeys: ['unitMap.view'],
        migrationStatus: 'complete',
        notes: 'Unit Map authority is independent of Unit Health, Contact, import, and Unit-edit authority.',
    },
    {
        id: 'account-administration',
        area: 'Account administration',
        locations: [
            'src/App.tsx',
            'src/views/Settings.tsx',
            'src/hooks/useUserAccess.ts',
            'functions/src/userAccess.ts',
            'functions/src/entitlementResolution.ts',
            'firestore.rules',
        ],
        legacyMechanism: 'Tenant role === admin, with account-owner bootstrap remaining outside operational Positions.',
        capabilityKeys: [
            'accountAdministration.manageAdmins',
            'accountAdministration.viewEntitlementDiagnostics',
            'accountAdministration.viewLoginActivity',
        ],
        migrationStatus: 'pending',
        notes: 'Account administration remains direct, non-Position authority.',
    },
    {
        id: 'actions',
        area: 'Actions',
        locations: ['src/lib/visibility.ts', 'src/views/Actions.tsx', 'src/hooks/useScopedData.ts', 'firestore.rules'],
        legacyMechanism: 'Admin, Key 3, district, owner, participant, and membership predicates split between client helpers and Rules.',
        capabilityKeys: [
            'actions.comment',
            'actions.create',
            'actions.edit',
            'actions.manageParticipants',
            'actions.reassign',
            'actions.updateStatus',
            'actions.view',
        ],
        migrationStatus: 'pending',
        notes: 'Current Rules already distinguish the meaningful Action mutations represented here.',
    },
    {
        id: 'advancement',
        area: 'Advancement',
        locations: [
            'src/views/UnitHealth.tsx',
            'src/views/Settings.tsx',
            'src/hooks/useUnits.ts',
            'functions/src/advancementAutomation.ts',
            'firestore.rules',
        ],
        legacyMechanism: 'District/council read scoping plus tenant-admin-only preview and run-now callables.',
        capabilityKeys: ['advancement.previewAutomation', 'advancement.runAutomation', 'advancement.view'],
        migrationStatus: 'pending',
        notes: 'Scheduled reconciliation is internal and does not need a human capability.',
    },
    {
        id: 'application-settings',
        area: 'Application settings',
        locations: ['src/App.tsx', 'src/views/Settings.tsx', 'src/hooks/useSettings.ts', 'firestore.rules'],
        legacyMechanism: 'Admin route gate and tenant-admin Firestore writes.',
        capabilityKeys: ['applicationSettings.manage', 'applicationSettings.view'],
        migrationStatus: 'pending',
        notes: 'These account-level capabilities are deliberately unavailable to ordinary Position policy.',
    },
    {
        id: 'communications',
        area: 'Communications',
        locations: [
            'src/lib/visibility.ts',
            'src/views/Communications.tsx',
            'src/hooks/useCommunications.ts',
            'functions/src/communications.ts',
            'firestore.rules',
        ],
        legacyMechanism: 'Any Position holder may send in their district; admin is required for All Council.',
        capabilityKeys: [
            'communications.compose',
            'communications.send',
            'communications.view',
            'communications.viewContent',
        ],
        migrationStatus: 'complete',
        notes: 'Preview and send compile the active V2 policy server-side. Recipient addresses remain server-only and communications.send does not inherit Contact, FOS, or import authority.',
    },
    {
        id: 'contacts',
        area: 'Contacts and roster',
        locations: [
            'src/lib/visibility.ts',
            'src/views/Contacts.tsx',
            'src/views/OrgChart.tsx',
            'src/hooks/useScopedData.ts',
            'functions/src/userAccess.ts',
            'firestore.rules',
        ],
        legacyMechanism: 'District read; admin, Key 3, or committee-chair roster mutation tiers.',
        capabilityKeys: [
            'contacts.create',
            'contacts.delete',
            'contacts.edit',
            'contacts.managePositions',
            'contacts.view',
            'contacts.viewContactInfo',
        ],
        migrationStatus: 'pending',
        notes: 'Current UI exposes contact details with roster visibility; viewContactInfo preserves the future redaction boundary.',
    },
    {
        id: 'data-imports',
        area: 'Data imports',
        locations: [
            'src/lib/dataImports.ts',
            'src/views/Settings.tsx',
            'src/views/UnitHealth.tsx',
            'src/hooks/useUnits.ts',
            'firestore.rules',
        ],
        legacyMechanism: 'Import status is member-readable; uploads and import-management configuration are admin-only.',
        capabilityKeys: ['dataImports.manage', 'dataImports.perform', 'dataImports.viewStatus'],
        migrationStatus: 'pending',
        notes: 'Import authority is explicit and is not implied by district leadership or Key 3.',
    },
    {
        id: 'fos',
        area: 'Friends of Scouting',
        locations: [
            'functions/src/fos',
            'firestore.rules',
        ],
        legacyMechanism: 'None. The retired prototype and its membership-level fosAccess setting no longer exist. All FOS data is server-only (tenant-scoped subcollections, every client read and write denied by rules). Access comes from the compiled Permissions V2 projection: Position grants at district or council scope, published relationship-role grants for assigned managers, and the administration plane.',
        capabilityKeys: [
            'fos.administerCampaigns',
            'fos.administerGoals',
            'fos.administerSettings',
            'fos.anonymizeDonors',
            'fos.approveAdjustments',
            'fos.confirmPayments',
            'fos.correctContributions',
            'fos.exportDonations',
            'fos.exportSummary',
            'fos.manageAssignments',
            'fos.manageDonors',
            'fos.manageInteractions',
            'fos.managePledges',
            'fos.mergeDonors',
            'fos.printDonorSummary',
            'fos.produceRecognitionLists',
            'fos.reconcilePayments',
            'fos.reportPayments',
            'fos.reviewAudit',
            'fos.runImports',
            'fos.sendSolicitations',
            'fos.view',
            'fos.viewDonorContactInfo',
            'fos.viewDonorDetails',
            'fos.viewDonorHistory',
            'fos.viewRelationshipNotes',
        ],
        migrationStatus: 'pending',
        notes: 'FOS remains a distinct sensitive domain and is never bundled into Key 3, finance, or generic administration. Donor-management operations are enforced server-side once a council enables FOS; financial, output, goal, campaign, import, audit and merge capabilities are defined and grantable but have no operation yet. Campaign is a category, never a permission scope.',
    },
    {
        id: 'goals',
        area: 'Goals',
        locations: ['src/views/GoalsTasks.tsx', 'src/hooks/useScopedData.ts', 'src/lib/visibility.ts', 'firestore.rules'],
        legacyMechanism: 'Tenant members read; tenant admins create, edit, archive, and delete.',
        capabilityKeys: ['goals.archive', 'goals.create', 'goals.delete', 'goals.edit', 'goals.view'],
        migrationStatus: 'pending',
        notes: 'Goals are council strategy; capability and council scope remain separate facts.',
    },
    {
        id: 'initiatives-and-calendar',
        area: 'Initiatives and Calendar',
        locations: [
            'src/lib/visibility.ts',
            'src/hooks/useScopedData.ts',
            'src/views/Initiatives.tsx',
            'src/views/CalendarView.tsx',
            'src/lib/calendarGrid.ts',
            'firestore.rules',
        ],
        legacyMechanism: 'District/council Initiative queries and admin/committee-chair write gates; Calendar reuses the authorized result.',
        capabilityKeys: ['initiatives.create', 'initiatives.delete', 'initiatives.edit', 'initiatives.view'],
        migrationStatus: 'pending',
        notes: 'Calendar deliberately has no capability of its own and consumes initiatives.view plus its effective scope.',
    },
    {
        id: 'permissions-policy',
        area: 'Permissions administration',
        locations: ['src/App.tsx', 'src/views/Settings.tsx', 'functions/src/authz.ts', 'firestore.rules'],
        legacyMechanism: 'Tenant-admin route and data gates; Position policy does not exist yet.',
        capabilityKeys: ['permissions.manage', 'permissions.view'],
        migrationStatus: 'pending',
        notes: 'Permission-policy administration is account authority, not an operational Position bundle.',
    },
    {
        id: 'renewal',
        area: 'Renewal and recharter',
        locations: [
            'src/views/UnitHealth.tsx',
            'src/views/Settings.tsx',
            'src/hooks/useUnits.ts',
            'functions/src/rechartAutomation.ts',
            'firestore.rules',
        ],
        legacyMechanism: 'District/council read scoping plus tenant-admin-only preview and run-now callables.',
        capabilityKeys: ['renewal.previewAutomation', 'renewal.runAutomation', 'renewal.view'],
        migrationStatus: 'pending',
        notes: 'Nightly and import triggers are internal operations, not human capabilities.',
    },
    {
        id: 'strategy',
        area: 'Strategic plan',
        locations: ['src/views/StrategicPlan.tsx', 'src/hooks/useScopedData.ts', 'firestore.rules'],
        legacyMechanism: 'Tenant-member reads and tenant-admin writes.',
        capabilityKeys: ['strategy.manage', 'strategy.view'],
        migrationStatus: 'pending',
        notes: 'Strategy is operational council work, not account/system administration.',
    },
    {
        id: 'system-administration',
        area: 'Compass Manage and product administration',
        locations: [
            'functions/src/manageAuth.ts',
            'functions/src/backup.ts',
            'functions/src/entitlements.ts',
            'functions/src/entitlementResolution.ts',
            'firestore.rules',
        ],
        legacyMechanism: 'Separate active productMembership owner/admin plane.',
        capabilityKeys: [
            'systemAdministration.manageBackups',
            'systemAdministration.manageCustomers',
            'systemAdministration.manageEntitlements',
        ],
        migrationStatus: 'pending',
        notes: 'Product administration and licensing remain separate from tenant operational Position authorization.',
    },
    {
        id: 'tasks',
        area: 'Tasks',
        locations: [
            'src/lib/visibility.ts',
            'src/views/GoalsTasks.tsx',
            'src/views/Initiatives.tsx',
            'src/hooks/useScopedData.ts',
            'firestore.rules',
        ],
        legacyMechanism: 'District reads, committee-chair full editing, and assignee status/note updates.',
        capabilityKeys: [
            'tasks.assign',
            'tasks.comment',
            'tasks.create',
            'tasks.delete',
            'tasks.edit',
            'tasks.updateStatus',
            'tasks.view',
        ],
        migrationStatus: 'pending',
        notes: 'Assignment and assignee-limited workflow changes remain distinct authorization decisions.',
    },
    {
        id: 'training',
        area: 'Training and hazardous weather',
        locations: [
            'src/views/UnitHealth.tsx',
            'src/views/Settings.tsx',
            'src/hooks/useUnits.ts',
            'functions/src/sytAutomation.ts',
            'functions/src/hazardousWeatherAutomation.ts',
            'firestore.rules',
        ],
        legacyMechanism: 'District/council data reads plus tenant-admin-only automation preview and run-now callables.',
        capabilityKeys: ['training.previewAutomation', 'training.runAutomation', 'training.view'],
        migrationStatus: 'pending',
        notes: 'Internal scheduled/import-trigger execution is intentionally excluded from the human vocabulary.',
    },
    {
        id: 'units-and-exports',
        area: 'Units, Unit Health, commissioners, and exports',
        locations: [
            'src/views/UnitHealth.tsx',
            'src/views/Commissioners.tsx',
            'src/views/Dashboard.tsx',
            'src/views/dashboard/HealthSections.tsx',
            'src/views/dashboard/SeasonalSalePanel.tsx',
            'src/hooks/useUnits.ts',
            'src/lib/reports/unitExportPermissions.ts',
            'functions/src/districtSummaries.ts',
            'firestore.rules',
        ],
        legacyMechanism: 'District/council reads, literal District Commissioner Position checks, export predicates, and admin recompute.',
        capabilityKeys: ['units.export', 'units.manageCommissionerAssignments', 'units.recomputeHealth', 'units.view', 'units.viewCommissionerCoverage'],
        migrationStatus: 'pending',
        notes: 'Unit Health is a presentation area; capabilities follow its underlying security boundaries.',
    },
    {
        id: 'operational-user-access',
        area: 'Operational access grants',
        locations: [
            'src/lib/visibility.ts',
            'src/views/Contacts.tsx',
            'src/views/OrgChart.tsx',
            'src/hooks/useUserAccess.ts',
            'functions/src/userAccess.ts',
            'firestore.rules',
        ],
        legacyMechanism: 'Key 3/admin and chair grant tiers derived from current membership booleans and roles.',
        capabilityKeys: ['userAccess.manageOperational', 'userAccess.view'],
        migrationStatus: 'pending',
        notes: 'Operational access management is distinct from account-admin bootstrap and permission-policy management.',
    },
]);
/**
 * Known files that temporarily contain V1 authorization interpretation.
 * The static guard permits debt reduction inside these locations but fails
 * when a new file introduces a role, Key 3, Position-name, email-allowlist,
 * tenant-admin, or product-admin authorization check.
 */
export const LEGACY_AUTHORIZATION_FILE_ALLOWLIST = Object.freeze([
    'firestore.rules',
    'functions/src/advancementAutomation.ts',
    'functions/src/authz.ts',
    'functions/src/backup.ts',
    'functions/src/communications.ts',
    'functions/src/contactVisibility.ts',
    'functions/src/districtSummaries.ts',
    'functions/src/entitlementResolution.ts',
    'functions/src/entitlements.ts',
    'functions/src/hazardousWeatherAutomation.ts',
    'functions/src/manageAuth.ts',
    'functions/src/rechartAutomation.ts',
    'functions/src/sytAutomation.ts',
    'functions/src/userAccess.ts',
    'functions/src/unitMap.ts',
    'src/App.tsx',
    'src/components/Layout.tsx',
    'src/hooks/useCommunications.ts',
    'src/hooks/useInitiativeMessages.ts',
    'src/hooks/useScopedData.ts',
    'src/hooks/useUnits.ts',
    'src/hooks/useUserAccess.ts',
    'src/lib/dataImports.ts',
    'src/lib/reports/unitExportPermissions.ts',
    'src/lib/visibility.ts',
    'src/views/Actions.tsx',
    'src/views/Commissioners.tsx',
    'src/views/Communications.tsx',
    'src/views/Contacts.tsx',
    'src/views/Dashboard.tsx',
    'src/views/GoalsTasks.tsx',
    'src/views/Initiatives.tsx',
    'src/views/OrgChart.tsx',
    'src/views/Settings.tsx',
    'src/views/StrategicPlan.tsx',
    'src/views/UnitHealth.tsx',
    'src/views/dashboard/HealthSections.tsx',
    'src/views/dashboard/SeasonalSalePanel.tsx',
]);
export function isApprovedLegacyAuthorizationFile(path) {
    return LEGACY_AUTHORIZATION_FILE_ALLOWLIST.includes(path);
}
